// Scheduling (CAERA-native) — the office's own week board.
//
// Reads and writes CAERA's schedules/visits tables only. The AxisCare mirror
// (service_visits) and the read-only /hh/schedule page are untouched; this
// page is gated behind the `scheduling` module so it stays invisible to
// everyone but admins until the shadow/pilot phases say otherwise.
//
// Three things live here: the week grid (by client or by caregiver), the
// recurring-schedule editor, and the visit drawer where a coordinator
// reassigns, re-times, cancels or marks a visit missed — every such change
// needs a modification reason, which the DB trigger writes to visit_events.
import { useCallback, useEffect, useMemo, useState } from 'react';
import CoverageBoard from '../../components/scheduling/CoverageBoard';
import { format, addDays, startOfWeek, isSameDay, parseISO } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import useIsMobile from '../../hooks/useIsMobile';
import { schedulingGuard, schedulingGuardMark } from '../../lib/staffCompliance';
import { supabase } from '../../lib/supabase';
import {
  getServiceCatalog, getActiveClients, getFieldStaff, getSchedules, createSchedule, updateSchedule,
  getVisitsInRange, updateVisit, getVisitEvents, getConflicts, regenerateAllVisits,
  clientLabel, staffLabel, visitHours, DAY_LABELS, FREQUENCIES, MISSED_REASONS, MISSED_ACTIONS,
  STATUS_LABEL, SERVICE_COLOR, getShiftClaims, acceptShiftClaim, declineShiftClaim, getTimeOffRequests, decideTimeOff, getApprovedTimeOff,
  evvStatusFor,
} from '../../lib/scheduling';
import { petConflict } from '../../lib/pets';

const iso = (d) => format(d, 'yyyy-MM-dd');
const hhmm = (s) => format(parseISO(s), 'h:mmaaa').replace(':00', '');
const field = { width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 11, color: 'var(--text3)', marginBottom: 5 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' };
const sheet = (w) => ({ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: w, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' });
const sheetHead = { padding: '14px 20px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
const errBox = { background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '10px 14px', margin: '12px 0', fontSize: 12, color: 'var(--danger)' };
const warnBox = { background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 7, padding: '10px 14px', margin: '12px 0', fontSize: 12, color: 'var(--warn)' };

// Tab: the name on one line, the supporting counts smaller underneath, so it
// reads as one segmented control rather than four buttons in a box.
const segTab = (active, edge) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
  padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', minWidth: 0,
  border: 'none',
  borderLeft: edge.left ? '0.5px solid var(--border)' : 'none',
  borderTop: edge.top ? '0.5px solid var(--border)' : 'none',
  background: active ? 'var(--accent)' : 'transparent',
  color: active ? 'var(--white)' : 'var(--text)',
});
const segSub = (active) => ({
  fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  maxWidth: '100%', color: active ? 'var(--white)' : 'var(--text3)', opacity: active ? 0.78 : 1,
});

const EMPTY_SCHEDULE = {
  client_id: '', service_id: '', staff_id: '', days_of_week: [1, 2, 3, 4, 5],
  start_time: '08:00', end_time: '16:00', frequency_weeks: 1,
  start_date: iso(new Date()), end_date: '', notes: '',
};

const statusStyle = (v) => {
  if (v.status === 'cancelled' || v.status === 'missed') return { bg: 'var(--danger-bg)', bd: 'var(--danger-border)', fg: 'var(--danger)' };
  if (v.status === 'completed') return { bg: 'var(--success-bg)', bd: 'var(--success-border)', fg: 'var(--success)' };
  if (v.status === 'in_progress') return { bg: 'var(--info-bg)', bd: 'var(--info-border)', fg: 'var(--info)' };
  if (!v.staff_id) return { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: 'var(--warn)' };
  return { bg: 'var(--accent-bg)', bd: 'var(--accent-border)', fg: 'var(--accent2)' };
};

export default function Scheduling() {
  const { user, profile } = useAuth();
  const isMobile = useIsMobile();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [groupBy, setGroupBy] = useState('client');
  const [serviceFilter, setServiceFilter] = useState('');
  const [tab, setTab] = useState(() => (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab') === 'coverage') ? 'coverage' : 'week');

  const [catalog, setCatalog] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  // Policy A-2.1: a flagged background check, or GCHEXS still pending after
  // the 30-day provisional window, stops an assignment outright; other open
  // compliance items are confirmed past. Returns an error string to show, or
  // null when it is fine to proceed.
  const guardAssignment = (staffId, clientId) => {
    if (!staffId) return null;
    const person = staff.find(x => x.id === staffId);
    const g = schedulingGuard(person);
    if (g) {
      if (g.level === 'block') return `${g.label} — ${g.detail}`;
      if (!window.confirm(`${g.label}\n\n${g.detail}\n\nAssign anyway?`)) return 'Assignment cancelled.';
    }
    // A warning, not a block: the coordinator may know the pet is kept apart.
    const pets = petConflict(person, clients.find(c => c.id === clientId));
    if (pets && !window.confirm(`Household pets\n\n${pets}\n\nAssign anyway?`)) return 'Assignment cancelled.';
    return null;
  };
  const [schedules, setSchedules] = useState([]);
  const [visits, setVisits] = useState([]);
  const [claims, setClaims] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [vTimeOff, setVTimeOff] = useState([]);
  const [openOffers, setOpenOffers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [schedModal, setSchedModal] = useState(false);
  const [editingSched, setEditingSched] = useState(null);
  const [sform, setSform] = useState(EMPTY_SCHEDULE);
  const [sformError, setSformError] = useState('');

  const [drawer, setDrawer] = useState(null);      // the visit being edited
  const [vform, setVform] = useState(null);
  const [vevents, setVevents] = useState([]);
  const [vconflicts, setVconflicts] = useState([]);
  const [vformError, setVformError] = useState('');

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const loadStatic = useCallback(async () => {
    const [cat, cl, st, sc] = await Promise.all([getServiceCatalog(), getActiveClients(), getFieldStaff(), getSchedules()]);
    const err = cat.error || cl.error || st.error || sc.error;
    if (err) setError(err.message);
    setCatalog(cat.data || []); setClients(cl.data || []); setStaff(st.data || []); setSchedules(sc.data || []);
  }, []);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    const [{ data, error: err }, cl, to] = await Promise.all([
      getVisitsInRange(`${iso(weekStart)}T00:00:00`, `${iso(addDays(weekStart, 7))}T00:00:00`),
      // Requests are not week-bound: a caregiver asks for shifts up to three
      // weeks out, and the office should see every open request in one list.
      getShiftClaims(`${iso(new Date())}T00:00:00`, `${iso(addDays(new Date(), 28))}T00:00:00`),
      getTimeOffRequests('requested'),
    ]);
    if (err) setError(err.message);
    setVisits(data || []);
    setClaims(cl.data || []);
    setTimeOff(to.data || []);
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  // Only for the tab subtitle; Agency Scheduling loads the offers themselves.
  useEffect(() => {
    supabase.from('client_agency_offers').select('id', { count: 'exact', head: true }).eq('status', 'open')
      .then(({ count }) => setOpenOffers(count ?? 0));
  }, []);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  // ── Derived ─────────────────────────────────────────────────────────────
  const shown = useMemo(() => serviceFilter ? visits.filter(v => v.service_id === serviceFilter) : visits, [visits, serviceFilter]);
  const live = shown.filter(v => v.status !== 'cancelled');
  const stats = {
    visits: live.length,
    hours: live.reduce((s, v) => s + visitHours(v), 0),
    open: live.filter(v => !v.staff_id).length,
    openHours: live.filter(v => !v.staff_id).reduce((s, v) => s + visitHours(v), 0),
    clients: new Set(live.map(v => v.client_id)).size,
    caregivers: new Set(live.filter(v => v.staff_id).map(v => v.staff_id)).size,
  };

  // Each tab carries its own supporting line: the name reads first, the counts
  // sit under it a size down so they are clearly just context.
  const tabCols = isMobile ? 2 : 4;
  const TABS = useMemo(() => {
    const active = schedules.filter(x => x.status === 'active').length;
    const paused = schedules.filter(x => x.status === 'paused').length;
    const waiting = claims.length + timeOff.length;
    return [
      ['week', 'Week board', `${format(weekStart, 'd MMM')} – ${format(weekEnd, 'd MMM')}`],
      ['schedules', 'Recurring schedules', `${active} active · ${paused} paused`],
      ['requests', 'Requests', waiting ? `${waiting} waiting` : 'none waiting'],
      ['coverage', 'Agency scheduling', openOffers === null ? '\u2014' : (openOffers ? `${openOffers} open offer${openOffers === 1 ? '' : 's'}` : 'no open offers')],
    ];
  }, [schedules, claims.length, timeOff.length, weekStart, weekEnd, openOffers]);

  const rows = useMemo(() => {
    const map = new Map();
    shown.forEach(v => {
      const key = groupBy === 'client' ? v.client_id : (v.staff_id || 'open');
      const labelText = groupBy === 'client' ? clientLabel(v.client) : (v.staff_id ? staffLabel(v.staff) : 'Open shifts');
      if (!map.has(key)) map.set(key, { key, label: labelText, visits: [] });
      map.get(key).visits.push(v);
    });
    return [...map.values()].sort((a, b) => a.key === 'open' ? -1 : b.key === 'open' ? 1 : a.label.localeCompare(b.label));
  }, [shown, groupBy]);

  const staffForService = (serviceId) => {
    const svc = catalog.find(c => c.id === serviceId);
    if (!svc || !svc.caregiver_types?.length) return staff;
    return staff.filter(s => svc.caregiver_types.includes(s.position_code));
  };

  // ── Schedule editor ─────────────────────────────────────────────────────
  const openNewSchedule = () => { setEditingSched(null); setSform({ ...EMPTY_SCHEDULE, start_date: iso(weekStart > new Date() ? weekStart : new Date()) }); setSformError(''); setSchedModal(true); };
  const openEditSchedule = (s) => {
    setEditingSched(s);
    setSform({
      client_id: s.client_id, service_id: s.service_id, staff_id: s.staff_id || '',
      days_of_week: s.days_of_week || [], start_time: (s.start_time || '').slice(0, 5), end_time: (s.end_time || '').slice(0, 5),
      frequency_weeks: s.frequency_weeks || 1, start_date: s.start_date, end_date: s.end_date || '', notes: s.notes || '',
    });
    setSformError(''); setSchedModal(true);
  };
  const sset = (k, v) => setSform(f => ({ ...f, [k]: v }));
  const toggleDay = (d) => setSform(f => ({ ...f, days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter(x => x !== d) : [...f.days_of_week, d].sort() }));
  const schedHours = (() => {
    if (!sform.start_time || !sform.end_time) return 0;
    const [sh, sm] = sform.start_time.split(':').map(Number), [eh, em] = sform.end_time.split(':').map(Number);
    let h = (eh + em / 60) - (sh + sm / 60); if (h <= 0) h += 24; return Math.round(h * 100) / 100;
  })();

  const saveSchedule = async () => {
    if (!sform.client_id) return setSformError('Pick a client.');
    if (!sform.service_id) return setSformError('Pick a service line.');
    if (!sform.days_of_week.length) return setSformError('Pick at least one day of the week.');
    if (!sform.start_time || !sform.end_time) return setSformError('Start and end times are required.');
    if (!sform.start_date) return setSformError('A start date is required.');
    if (sform.end_date && sform.end_date < sform.start_date) return setSformError('End date is before the start date.');
    if ((sform.staff_id || null) !== (editingSched?.staff_id || null)) {
      const blocked = guardAssignment(sform.staff_id, sform.client_id);
      if (blocked) return setSformError(blocked);
    }
    setBusy(true); setSformError('');
    const payload = {
      client_id: sform.client_id, service_id: sform.service_id, staff_id: sform.staff_id || null,
      days_of_week: sform.days_of_week, start_time: sform.start_time, end_time: sform.end_time,
      frequency_weeks: Number(sform.frequency_weeks), start_date: sform.start_date, end_date: sform.end_date || null,
      notes: sform.notes || null,
    };
    const { error: err } = editingSched
      ? await updateSchedule(editingSched.id, payload)
      : await createSchedule({ ...payload, status: 'active', created_by: user?.id || null, created_by_name: profile?.full_name || null });
    setBusy(false);
    if (err) return setSformError(err.message);
    setSchedModal(false);
    await Promise.all([loadStatic(), loadWeek()]);
  };

  const setScheduleStatus = async (s, status) => {
    setBusy(true);
    const { error: err } = await updateSchedule(s.id, { status });
    setBusy(false);
    if (err) return setError(err.message);
    await Promise.all([loadStatic(), loadWeek()]);
  };

  const regenerate = async () => {
    setBusy(true);
    const { data, error: err } = await regenerateAllVisits(28);
    setBusy(false);
    if (err) return setError(err.message);
    setError('');
    await loadWeek();
    window.alert(`${data ?? 0} new visit${data === 1 ? '' : 's'} generated for the next 28 days.`);
  };

  // ── Visit drawer ────────────────────────────────────────────────────────
  const claimsFor = (visitId) => claims.filter(c => c.visit_id === visitId);
  const decideClaim = async (claim, accept) => {
    const note = accept ? null : (window.prompt('Reason for declining (optional):') ?? null);
    setBusy(true);
    const { error: err } = accept ? await acceptShiftClaim(claim.id, null) : await declineShiftClaim(claim.id, note);
    setBusy(false);
    if (err) return setVformError(err.message);
    setDrawer(null); await loadWeek();
  };
  const decideOff = async (req, approve) => {
    const note = window.prompt(approve ? 'Note for the caregiver (optional):' : 'Reason for denying (optional):') ?? null;
    setBusy(true);
    const { error: err } = await decideTimeOff(req.id, approve, note);
    setBusy(false);
    if (err) return setError(err.message);
    await loadWeek();
  };

  const openVisit = async (v) => {
    setDrawer(v);
    setVform({
      staff_id: v.staff_id || '', start_time: format(parseISO(v.scheduled_start), 'HH:mm'), end_time: format(parseISO(v.scheduled_end), 'HH:mm'),
      status: v.status, missed_reason_code: v.missed_reason_code || '', missed_action_code: v.missed_action_code || '',
      modification_reason: '', care_note: v.care_note || '',
    });
    setVformError(''); setVconflicts([]);
    const { data } = await getVisitEvents(v.id);
    setVevents(data || []);
  };
  const vset = (k, val) => setVform(f => ({ ...f, [k]: val }));

  const visitTimes = () => {
    const day = format(parseISO(drawer.scheduled_start), 'yyyy-MM-dd');
    const start = new Date(`${day}T${vform.start_time}:00`);
    let end = new Date(`${day}T${vform.end_time}:00`);
    if (end <= start) end = addDays(end, 1);
    return { start, end };
  };

  useEffect(() => {
    if (!drawer || !vform?.staff_id) { setVconflicts([]); return; }
    const { start, end } = visitTimes();
    getConflicts(vform.staff_id, start.toISOString(), end.toISOString(), drawer.id).then(({ data }) => setVconflicts(data || []));
    getApprovedTimeOff(vform.staff_id, iso(start), iso(end)).then(({ data }) => setVTimeOff(data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer, vform?.staff_id, vform?.start_time, vform?.end_time]);

  const saveVisit = async () => {
    const { start, end } = visitTimes();
    const patch = {};
    if ((vform.staff_id || null) !== (drawer.staff_id || null)) {
      const blocked = guardAssignment(vform.staff_id, drawer.client_id);
      if (blocked) return setVformError(blocked);
      patch.staff_id = vform.staff_id || null;
    }
    if (start.toISOString() !== new Date(drawer.scheduled_start).toISOString()) patch.scheduled_start = start.toISOString();
    if (end.toISOString() !== new Date(drawer.scheduled_end).toISOString()) patch.scheduled_end = end.toISOString();
    if (vform.status !== drawer.status) {
      patch.status = vform.status;
      patch.evv_status = evvStatusFor(vform.status);
    }
    // EVV lines follow the Georgia attestation: every office edit carries a
    // reason and a missed visit carries its codes. Non-EVV lines are edited
    // freely (the audit trail still records who changed what).
    const evv = !!drawer.service?.evv_required;
    if (vform.status === 'missed') {
      if (evv && !vform.missed_reason_code) return setVformError('A missed visit on an EVV line needs a reason code.');
      if (evv && !vform.missed_action_code) return setVformError('Record the action taken for the missed visit.');
      patch.missed_reason_code = vform.missed_reason_code || null; patch.missed_action_code = vform.missed_action_code || null;
    }
    if ((vform.care_note || '') !== (drawer.care_note || '')) patch.care_note = vform.care_note || null;
    if (Object.keys(patch).length === 0) return setVformError('Nothing changed.');
    if (evv && !vform.modification_reason.trim()) return setVformError('Every change to a visit on an EVV line needs a reason. It is written to the audit trail.');
    patch.modification_reason = vform.modification_reason.trim() || null;
    patch.updated_by = user?.id || null;
    setBusy(true); setVformError('');
    const { error: err } = await updateVisit(drawer.id, patch);
    setBusy(false);
    if (err) return setVformError(err.message);
    setDrawer(null);
    await loadWeek();
  };

  // ── Render helpers ──────────────────────────────────────────────────────
  const VisitChip = ({ v }) => {
    const st = statusStyle(v);
    const who = groupBy === 'client' ? (v.staff_id ? staffLabel(v.staff) : 'Open') : clientLabel(v.client).split(' · ')[0];
    return (
      <button onClick={() => openVisit(v)} title={`${clientLabel(v.client)} · ${staffLabel(v.staff)} · ${v.service?.name}`}
        style={{ display: 'block', width: '100%', textAlign: 'left', background: st.bg, border: `1px solid ${st.bd}`, borderRadius: 6,
          padding: '4px 6px', marginBottom: 4, cursor: 'pointer', fontFamily: 'inherit',
          textDecoration: v.status === 'cancelled' ? 'line-through' : 'none', opacity: v.status === 'cancelled' ? 0.7 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: st.fg, fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: 2, background: SERVICE_COLOR[v.service?.color] || SERVICE_COLOR.other, flexShrink: 0 }} />
          {hhmm(v.scheduled_start)}–{hhmm(v.scheduled_end)}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{who}{!v.staff_id && claimsFor(v.id).length ? ` · ${claimsFor(v.id).length} req` : ''}</div>
      </button>
    );
  };

  const serviceOptions = catalog.map(c => <option key={c.id} value={c.id}>{c.name}</option>);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 560, lineHeight: 1.5 }}>
          CAERA's own schedule. Recurring patterns generate visits 28 days ahead; caregivers will clock against these
          visits from the Caera app. Nothing here changes AxisCare.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={regenerate} disabled={busy}>↻ Roll horizon forward</button>
          <button className="btn btn-primary" onClick={openNewSchedule}>+ New recurring schedule</button>
        </div>
      </div>

      {error && <div style={errBox}>⚠ {error}</div>}

      {/* Tabs + week nav */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tabCols}, minmax(0, 1fr))`, flex: '1 1 100%', maxWidth: isMobile ? '100%' : 660, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--card-bg)' }}>
          {TABS.map(([k, name, sub], i) => (
            <button key={k} onClick={() => setTab(k)} style={segTab(tab === k, { left: i % tabCols !== 0, top: i >= tabCols })}>
              <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{name}</span>
              <span style={segSub(tab === k)}>{sub}</span>
            </button>
          ))}
        </div>
        {tab === 'week' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              <button className="btn" onClick={() => setWeekStart(d => addDays(d, -7))}>← Prev</button>
              <button className="btn" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))}>This week</button>
              <button className="btn" onClick={() => setWeekStart(d => addDays(d, 7))}>Next →</button>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginLeft: 6 }}>{format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}</span>
            </div>
            <select style={{ ...field, width: 'auto' }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="client">Rows: clients</option>
              <option value="staff">Rows: caregivers</option>
            </select>
            <select style={{ ...field, width: 'auto' }} value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
              <option value="">All service lines</option>
              {serviceOptions}
            </select>
          </>
        )}
      </div>

      {tab === 'week' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
            {[['Visits', stats.visits], ['Hours', stats.hours.toFixed(1)], ['Open shifts', `${stats.open} · ${stats.openHours.toFixed(1)}h`], ['Clients', stats.clients], ['Caregivers', stats.caregivers]].map(([l, n]) => (
              <div key={l} style={{ background: 'var(--card-bg)', padding: '10px 14px' }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: l === 'Open shifts' && stats.open ? 'var(--warn)' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{n}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l}</div>
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No CAERA visits this week. Add a recurring schedule, or roll the horizon forward if schedules exist.
            </div>
          ) : (
            <div className="card" style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900, tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: 180, textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--text3)', borderBottom: '0.5px solid var(--border)', background: 'var(--accent-bg)', position: 'sticky', left: 0 }}>{groupBy === 'client' ? 'Client' : 'Caregiver'}</th>
                    {weekDays.map(d => (
                      <th key={iso(d)} style={{ textAlign: 'left', padding: '8px 8px', fontSize: 11, borderBottom: '0.5px solid var(--border)', background: 'var(--accent-bg)', color: isSameDay(d, new Date()) ? 'var(--accent2)' : 'var(--text3)' }}>
                        {format(d, 'EEE d')}{isSameDay(d, new Date()) ? ' · today' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key}>
                      <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: r.key === 'open' ? 'var(--warn)' : 'var(--text)', borderBottom: '0.5px solid var(--border)', verticalAlign: 'top', background: 'var(--card-bg)', position: 'sticky', left: 0 }}>
                        {r.label}
                        <div style={{ fontSize: 10.5, color: 'var(--text3)', fontWeight: 400 }}>{r.visits.filter(v => v.status !== 'cancelled').reduce((s, v) => s + visitHours(v), 0).toFixed(1)} h</div>
                      </td>
                      {weekDays.map(d => (
                        <td key={iso(d)} style={{ padding: 6, borderBottom: '0.5px solid var(--border)', borderLeft: '0.5px solid var(--border)', verticalAlign: 'top', background: isSameDay(d, new Date()) ? 'var(--bg2)' : 'transparent' }}>
                          {r.visits.filter(v => isSameDay(parseISO(v.scheduled_start), d)).map(v => <VisitChip key={v.id} v={v} />)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'schedules' && (
        <div className="card">
          {schedules.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No recurring schedules yet.</div>
          ) : schedules.map(s => (
            <div key={s.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', opacity: s.status === 'paused' ? 0.6 : 1 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: SERVICE_COLOR[s.service?.color] || SERVICE_COLOR.other }} />
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{clientLabel(s.client)}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>{s.service?.name} · {s.staff_id ? staffLabel(s.staff) : <span style={{ color: 'var(--warn)' }}>Open</span>}</div>
              </div>
              <div style={{ flex: '1 1 220px', fontSize: 12, color: 'var(--text2)' }}>
                {(s.days_of_week || []).map(d => DAY_LABELS[d]).join(' ')} · {(s.start_time || '').slice(0, 5)}–{(s.end_time || '').slice(0, 5)} · {s.hours_per_visit}h/visit
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{FREQUENCIES.find(f => f.value === s.frequency_weeks)?.label} · from {format(parseISO(s.start_date), 'd MMM yy')}{s.end_date ? ` to ${format(parseISO(s.end_date), 'd MMM yy')}` : ''}{s.status === 'paused' ? ' · PAUSED' : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEditSchedule(s)}>Edit</button>
                {s.status === 'active'
                  ? <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setScheduleStatus(s, 'paused')} disabled={busy}>Pause</button>
                  : <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--success)' }} onClick={() => setScheduleStatus(s, 'active')} disabled={busy}>Resume</button>}
                <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)' }} onClick={() => window.confirm('End this schedule? Future unstarted visits are removed; past and started visits are kept.') && setScheduleStatus(s, 'ended')} disabled={busy}>End</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'coverage' && <CoverageBoard />}

      {tab === 'requests' && (
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="card">
            <div className="card-header"><div className="card-title">Shift requests (next 4 weeks)</div></div>
            {claims.length === 0 ? <div style={{ padding: 20, fontSize: 13, color: 'var(--text3)' }}>No open-shift requests for this week.</div> : claims.map(c => (
              <div key={c.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontWeight: 500 }}>{staffLabel(c.staff)} · {c.staff?.position_code}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>wants {clientLabel(c.visit?.client)} · {c.visit?.service?.name} · {format(parseISO(c.visit.scheduled_start), 'EEE d MMM h:mmaaa')}–{hhmm(c.visit.scheduled_end)}{c.note ? ` — “${c.note}”` : ''}</div>
                </div>
                <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} disabled={busy} onClick={() => decideClaim(c, true)}>Accept</button>
                <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)' }} disabled={busy} onClick={() => decideClaim(c, false)}>Decline</button>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-header"><div className="card-title">Time-off requests</div></div>
            {timeOff.length === 0 ? <div style={{ padding: 20, fontSize: 13, color: 'var(--text3)' }}>No pending time-off requests.</div> : timeOff.map(r => (
              <div key={r.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontWeight: 500 }}>{staffLabel(r.staff)} · {r.staff?.position_code}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.start_date === r.end_date ? format(parseISO(r.start_date), 'EEE d MMM yyyy') : `${format(parseISO(r.start_date), 'd MMM')} – ${format(parseISO(r.end_date), 'd MMM yyyy')}`}{r.reason ? ` — ${r.reason}` : ''}</div>
                </div>
                <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} disabled={busy} onClick={() => decideOff(r, true)}>Approve</button>
                <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)' }} disabled={busy} onClick={() => decideOff(r, false)}>Deny</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recurring schedule modal */}
      {schedModal && (
        <div style={overlay}>
          <div style={sheet(620)}>
            <div style={sheetHead}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{editingSched ? 'Edit recurring schedule' : 'New recurring schedule'}</div>
              <button onClick={() => setSchedModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button>
            </div>
            <div style={{ padding: '6px 20px 20px' }}>
              {sformError && <div style={errBox}>{sformError}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <div style={label}>Client</div>
                  <select style={field} value={sform.client_id} onChange={e => sset('client_id', e.target.value)} disabled={!!editingSched}>
                    <option value="">Choose…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
                  </select>
                </div>
                <div>
                  <div style={label}>Service line</div>
                  <select style={field} value={sform.service_id} onChange={e => { sset('service_id', e.target.value); sset('staff_id', ''); }}>
                    <option value="">Choose…</option>
                    {serviceOptions}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={label}>Caregiver</div>
                <select style={field} value={sform.staff_id} onChange={e => sset('staff_id', e.target.value)}>
                  <option value="">Open shift — assign later</option>
                  {staffForService(sform.service_id).map(s => <option key={s.id} value={s.id}>{staffLabel(s)} · {s.position_code}{schedulingGuardMark(s)}</option>)}
                </select>
                {sform.service_id && staffForService(sform.service_id).length < staff.length && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Only roles the service catalog allows for this line are listed.</div>
                )}
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={label}>Days</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {DAY_LABELS.map((d, i) => {
                    const on = sform.days_of_week.includes(i);
                    return <button key={d} type="button" className="btn" onClick={() => toggleDay(i)} style={{ padding: '5px 12px', fontSize: 12, background: on ? 'var(--accent)' : 'var(--bg2)', color: on ? 'var(--white)' : 'var(--text2)', border: on ? 'none' : '1px solid var(--border)' }}>{d}</button>;
                  })}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                <div><div style={label}>Start time</div><input type="time" style={field} value={sform.start_time} onChange={e => sset('start_time', e.target.value)} /></div>
                <div><div style={label}>End time</div><input type="time" style={field} value={sform.end_time} onChange={e => sset('end_time', e.target.value)} /></div>
                <div>
                  <div style={label}>Repeats</div>
                  <select style={field} value={sform.frequency_weeks} onChange={e => sset('frequency_weeks', Number(e.target.value))}>
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div><div style={label}>From</div><input type="date" style={field} value={sform.start_date} onChange={e => sset('start_date', e.target.value)} /></div>
                <div><div style={label}>Until (optional)</div><input type="date" style={field} value={sform.end_date} onChange={e => sset('end_date', e.target.value)} /></div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={label}>Notes for the office</div>
                <input style={field} value={sform.notes} onChange={e => sset('notes', e.target.value)} />
              </div>
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text2)', background: 'var(--bg2)', borderRadius: 7, padding: '8px 12px' }}>
                {schedHours}h per visit · {sform.days_of_week.length} day{sform.days_of_week.length === 1 ? '' : 's'} · <strong>{(schedHours * sform.days_of_week.length / (sform.frequency_weeks || 1)).toFixed(1)}h per week</strong>
                {sform.end_time && sform.start_time && sform.end_time <= sform.start_time && ' · overnight, ends next day'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <button className="btn" onClick={() => setSchedModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveSchedule} disabled={busy}>{busy ? 'Saving…' : editingSched ? 'Save & regenerate visits' : 'Create & generate visits'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visit drawer */}
      {drawer && vform && (
        <div style={overlay}>
          <div style={sheet(560)}>
            <div style={sheetHead}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{clientLabel(drawer.client)}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{drawer.evv_visit_id} · {drawer.service?.name} · {format(parseISO(drawer.scheduled_start), 'EEE d MMM yyyy')}</div>
              </div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button>
            </div>
            <div style={{ padding: '6px 20px 20px' }}>
              {vformError && <div style={errBox}>{vformError}</div>}
              {vconflicts.length > 0 && (
                <div style={warnBox}>
                  This caregiver is already booked at an overlapping time: {vconflicts.map(c => `${clientLabel(c.client).split(' · ')[0]} ${hhmm(c.scheduled_start)}–${hhmm(c.scheduled_end)}`).join('; ')}.
                </div>
              )}
              {vTimeOff.length > 0 && (
                <div style={warnBox}>This caregiver has approved time off covering this date{vTimeOff[0].reason ? ` (${vTimeOff[0].reason})` : ''}.</div>
              )}
              {!drawer.staff_id && claimsFor(drawer.id).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Shift requests</div>
                  {claimsFor(drawer.id).map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '0.5px solid var(--border)', fontSize: 12 }}>
                      <div style={{ flex: 1 }}><strong>{staffLabel(c.staff)}</strong> · {c.staff?.position_code}{c.note ? ` — “${c.note}”` : ''}<div style={{ fontSize: 11, color: 'var(--text3)' }}>{format(parseISO(c.created_at), 'd MMM h:mmaaa')}</div></div>
                      <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} disabled={busy} onClick={() => decideClaim(c, true)}>Accept</button>
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)' }} disabled={busy} onClick={() => decideClaim(c, false)}>Decline</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, fontSize: 12 }}>
                <span className="badge badge-normal">{STATUS_LABEL[drawer.status]}</span>
                <span className="badge badge-low">EVV {drawer.evv_status}</span>
                {drawer.actual_start && <span className="badge badge-done">Clocked in {hhmm(drawer.actual_start)}</span>}
                {drawer.actual_end && <span className="badge badge-done">Out {hhmm(drawer.actual_end)}</span>}
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={label}>Caregiver</div>
                <select style={field} value={vform.staff_id} onChange={e => vset('staff_id', e.target.value)} disabled={drawer.status === 'completed'}>
                  <option value="">Open — unassigned</option>
                  {staffForService(drawer.service_id).map(s => <option key={s.id} value={s.id}>{staffLabel(s)} · {s.position_code}{schedulingGuardMark(s)}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                <div><div style={label}>Start</div><input type="time" style={field} value={vform.start_time} onChange={e => vset('start_time', e.target.value)} disabled={drawer.status === 'completed'} /></div>
                <div><div style={label}>End</div><input type="time" style={field} value={vform.end_time} onChange={e => vset('end_time', e.target.value)} disabled={drawer.status === 'completed'} /></div>
                <div>
                  <div style={label}>Status</div>
                  <select style={field} value={vform.status} onChange={e => vset('status', e.target.value)}>
                    {['scheduled', 'cancelled', 'missed', 'in_progress', 'completed', 'incomplete'].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
              </div>
              {vform.status === 'missed' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  <div>
                    <div style={label}>Missed-visit reason</div>
                    <select style={field} value={vform.missed_reason_code} onChange={e => vset('missed_reason_code', e.target.value)}>
                      <option value="">Choose…</option>
                      {MISSED_REASONS.map(r => <option key={r.code} value={r.code}>{r.label} ({r.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={label}>Action taken</div>
                    <select style={field} value={vform.missed_action_code} onChange={e => vset('missed_action_code', e.target.value)}>
                      <option value="">Choose…</option>
                      {MISSED_ACTIONS.map(r => <option key={r.code} value={r.code}>{r.label} ({r.code})</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <div style={label}>Office note on this visit</div>
                <textarea style={{ ...field, minHeight: 90, lineHeight: 1.5 }} value={vform.care_note} onChange={e => vset('care_note', e.target.value)} />
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={label}>Reason for this change {drawer.service?.evv_required ? <span style={{ color: 'var(--danger)' }}>*</span> : <span style={{ color: 'var(--text3)' }}>(optional — EVV is off for this line)</span>}</div>
                <input style={field} value={vform.modification_reason} onChange={e => vset('modification_reason', e.target.value)} placeholder="e.g. Caregiver called out sick; replacement assigned" />
              </div>

              <div style={{ marginTop: 18 }}>
                <div style={{ ...label, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Audit trail</div>
                {vevents.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>No events yet. Generated from its recurring schedule.</div>
                ) : vevents.map(e => (
                  <div key={e.id} style={{ fontSize: 12, color: 'var(--text2)', padding: '6px 0', borderBottom: '0.5px solid var(--border)' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{format(parseISO(e.occurred_at), 'd MMM HH:mm')}</span>
                    {' '}<strong>{e.event_type.replace('_', ' ')}</strong>{e.actor?.full_name ? ` · ${e.actor.full_name}` : ''}{e.reason_code ? ` · ${e.reason_code}` : ''}
                    {e.changes && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{Object.entries(e.changes).filter(([k]) => k !== 'modification_reason').map(([k, c]) => `${k}: ${c.from ?? '—'} → ${c.to ?? '—'}`).join(' · ')}</div>}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <button className="btn" onClick={() => setDrawer(null)}>Close</button>
                <button className="btn btn-primary" onClick={saveVisit} disabled={busy}>{busy ? 'Saving…' : 'Save change'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
